/**
 * Admin User Setup Script
 * 
 * Run this in your browser console (F12) when logged in
 * This will check for existing admin users and create one if needed
 */

import { db } from '../config/firebase';
import { collection, getDocs, doc, setDoc, getDoc, query, where } from 'firebase/firestore';

export async function checkAdminUsers() {
  console.log('🔍 Checking for admin users...\n');
  
  try {
    // Check adminUsers collection
    const adminUsersRef = collection(db, 'adminUsers');
    const adminSnapshot = await getDocs(adminUsersRef);
    
    if (adminSnapshot.empty) {
      console.log('❌ No users found in adminUsers collection\n');
      
      // Check users collection for admin role
      console.log('🔍 Checking users collection for admin role...\n');
      const usersRef = collection(db, 'users');
      const adminQuery = query(usersRef, where('role', '==', 'admin'));
      const usersSnapshot = await getDocs(adminQuery);
      
      if (usersSnapshot.empty) {
        console.log('❌ No users with admin role found in users collection\n');
        return { hasAdmins: false, adminUsers: [], regularAdmins: [] };
      } else {
        console.log('✅ Found users with admin role:');
        const regularAdmins: any[] = [];
        usersSnapshot.forEach(doc => {
          const data = doc.data();
          console.log(`\n📋 UID: ${doc.id}`);
          console.log(`   Email: ${data.email}`);
          console.log(`   Name: ${data.fullName || data.displayName}`);
          console.log(`   Role: ${data.role}`);
          regularAdmins.push({ id: doc.id, ...data });
        });
        return { hasAdmins: true, adminUsers: [], regularAdmins };
      }
    } else {
      console.log('✅ Found admin users in adminUsers collection:');
      const adminUsers: any[] = [];
      adminSnapshot.forEach(doc => {
        const data = doc.data();
        console.log(`\n📋 UID: ${doc.id}`);
        console.log(`   Email: ${data.email}`);
        console.log(`   Name: ${data.fullName}`);
        console.log(`   Role: ${data.role}`);
        console.log(`   Active: ${data.isActive}`);
        adminUsers.push({ id: doc.id, ...data });
      });
      return { hasAdmins: true, adminUsers, regularAdmins: [] };
    }
  } catch (error: any) {
    console.error('❌ Error checking admin users:', error.message);
    throw error;
  }
}

export async function createAdminUser(userId: string, email: string, fullName: string) {
  console.log('\n🔧 Creating admin user...');
  console.log(`   UID: ${userId}`);
  console.log(`   Email: ${email}\n`);
  
  const adminData = {
    id: userId,
    uid: userId,
    email: email,
    fullName: fullName,
    role: 'super_admin',
    permissions: {
      manageUsers: true,
      manageOrders: true,
      manageServices: true,
      manageProviders: true,
      viewReports: true,
      manageSettings: true,
      manageFeedback: true,
      managePayments: true
    },
    isActive: true,
    createdAt: new Date().toISOString(),
    lastLogin: new Date().toISOString()
  };
  
  try {
    const adminDocRef = doc(db, 'adminUsers', userId);
    await setDoc(adminDocRef, adminData);
    console.log('✅ Admin user created successfully!');
    console.log('\n🎉 You can now access the admin panel at /admin');
    return true;
  } catch (error: any) {
    console.error('❌ Error creating admin user:', error.message);
    throw error;
  }
}

export async function migrateUserToAdmin(userId: string) {
  console.log('\n🔄 Migrating user from users collection to adminUsers...');
  
  try {
    // Get user data
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      throw new Error('User not found');
    }
    
    const userData = userDoc.data();
    
    // Create admin user
    await createAdminUser(
      userId,
      userData.email,
      userData.fullName || userData.displayName || 'Admin User'
    );
    
    console.log('✅ Migration complete!');
    return true;
  } catch (error: any) {
    console.error('❌ Error migrating user:', error.message);
    throw error;
  }
}

// Make functions available in window for console use
if (typeof window !== 'undefined') {
  (window as any).checkAdminUsers = checkAdminUsers;
  (window as any).createAdminUser = createAdminUser;
  (window as any).migrateUserToAdmin = migrateUserToAdmin;
}

export default {
  checkAdminUsers,
  createAdminUser,
  migrateUserToAdmin
};
